import { CommonModule } from '@angular/common';
import { Component, inject, input, InputSignal, signal, SimpleChanges, WritableSignal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MaterialModule } from '../../material/material.module';
import { JobsService } from '../../services/jobs.service';
import { finalize, switchMap } from 'rxjs';
import { injectStripe, StripeServiceInterface } from 'ngx-stripe';
import { environment } from '../../../environments/environment';
import { MatSnackBar } from '@angular/material/snack-bar';

interface QuicktCreditOptions {
  credits: number;
  price: number; 
  discount?: any
}

@Component({
  selector: 'app-billing',
  imports: [CommonModule, ReactiveFormsModule, MaterialModule],
  templateUrl: './billing.component.html',
  styleUrl: './billing.component.scss'
})
export class BillingComponent {
  private readonly _fb = inject(FormBuilder)
  private readonly _api = inject(JobsService)
  private readonly _snackbar = inject(MatSnackBar)

  tab: InputSignal<'buy' | 'billing'> = input<'buy' | 'billing'>('buy');
  loading: WritableSignal<boolean> = signal(false);

  checkoutForm: FormGroup = this._fb.group({});

  stripe: StripeServiceInterface = injectStripe(environment.stripePublishableKey);
  selectedCredit: WritableSignal<number> = signal(0);
  creditOptions: QuicktCreditOptions[] = [
    {
      credits: 10,
      price: 100,
    },
    {
      credits: 20,
      price: 200,
    },
    {
      credits: 50,
      price: 500,
    },
    {
      credits: 100,
      price: 1000,
    },
  ]
  transactionsData: WritableSignal<any[]> = signal([]);

  ngOnInit(): void {
    this.initializeForm()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.tab() == 'billing') {
      this.fetchTransactionHistory();
    }
  }

    // Update form validation
  initializeForm() {
    this.checkoutForm = this._fb.group({
      quantity: ['', [
        Validators.required,
        Validators.min(1),
        Validators.max(10000),
        Validators.pattern(/^\d+$/)
      ]]
    });

    // Clear quick select when user types custom amount
    this.checkoutForm.get('quantity')?.valueChanges.subscribe((value: any) => {
      if (value && value !== this.selectedCredit()) {
        this.selectedCredit.set(0);
      }
    });
  }

  fetchTransactionHistory() {
    if (this.loading()) return;
    this.loading.set(true);
    this._api.getBillingHistory()
      .pipe(finalize(() => (this.loading.set(false))))
      .subscribe({
        next: (res: any) => {
          this.transactionsData.set(res.data.invoices.data)
        },
        error: (err: any) => {
          this._snackbar.open(err?.error?.message || 'Unable to fetch transaction history.')
        }
      })
  }

  // Quick select credits
  selectCredits(amount: number) {
    this.selectedCredit.set(amount);
    this.checkoutForm.patchValue({ quantity: amount });
  }

  // Calculate rate per credit based on quantity
  getRate(): number {
    // const quantity = this.checkoutForm.value.quantity || 0;
    // if (quantity >= 1000) return 9; // 10% discount
    // if (quantity >= 500) return 9.5; // 5% discount
    return 10; // Regular rate
  }

  // Get discount percentage if provided
  getDiscountPercentage(): number {
    // const quantity = this.checkoutForm.value.quantity || 0;
    // if (quantity >= 1000) return 10;
    // if (quantity >= 500) return 5;
    return 0;
  }

  // Calculate discount amount if discount provided
  getDiscount(): number {
    const quantity = this.checkoutForm.value.quantity || 0;
    const regularTotal = quantity * 10;
    const discountedTotal = quantity * this.getRate();
    return regularTotal - discountedTotal;
  }

  // Calculate total amount
  getTotalAmount(): number {
    const quantity = this.checkoutForm.value.quantity || 0;
    return quantity * this.getRate();
  }

  onSubmit() {
    if (this.loading()) return;
    this.loading.set(true);
    this._api.checkout(this.checkoutForm.value)
      .pipe(
        finalize(() => (this.loading.set(false))),
        switchMap((session: any) => {
          return this.stripe.redirectToCheckout({ sessionId: session.data.session_id })
        })
      )
      .subscribe(result => {
        if (result.error) {
          alert(result.error.message);
        }
      })
  }
}
